import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Modal, TouchableOpacity, ScrollView, StyleSheet, Image } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { api } from '../../../services/api';
import { storage } from '../../../utils/storage';
import { useLiveCapabilities } from '../../../hooks/useLiveCapabilities';
import {
  CAPABILITY_LABELS,
  liveCapabilityHighWaterKey,
  selectNewCapabilities,
  selectUpcomingUnlocks,
} from '../../../services/liveCapabilityDelta';
import {
  capabilityContext,
  trackCapabilitiesLeveledUp,
  trackCapabilityUnlockShown,
} from '../../../services/liveCapabilityAnalytics';
import type { GamificationEvent, GamificationLevel } from '../../../types/auth';
// CORE-509 — this popup is now mounted at the app root, outside the navigator,
// so it navigates through the container ref rather than `useNavigation`.
import { navigateTo } from '../../../navigation/rootNavigation';
import { GamificationShareButton } from '../../../components/gamification/GamificationShareButton';

/**
 * CORE-272 — «Поздравительный экран получения уровня»: the celebration screen
 * shown when the user reaches a new spiritual level. Triggered by a `level_up`
 * congratulation event (CORE-256, surfaced via useGamificationCelebration); dismissing it
 * acks the event. Shows everything the ticket asks for: название уровня, новый
 * бейдж, прогресс, полученные возможности, and a share button (reusing the app's
 * ShareModal → Telegram / WhatsApp / copy). Theme-aware.
 *
 * The event carries BE-rendered copy (title/message) + the level code
 * (referenceKey). CORE-946 — the celebrated level (name, badge, colour) is keyed
 * off that referenceKey, NOT the live profile: a QA-injected event (CORE-900) or
 * one that outran the profile refresh must still congratulate the level it names.
 * The profile is fetched on open only to feed the XP progress bar, and only when
 * it is already at that same level (see `resolveLevelUpPresentation`).
 */

/**
 * CORE-272 (Veronika, 2026-07-30) — the level medallion comes from Canonical v4
 * and nothing else: same PNG in both themes, never recoloured, cropped or
 * effect-laden, always one CSS size. The set ships ONE «уровень» medallion
 * (shield-star), so every level shows it — inventing a per-level look-alike is
 * exactly what the icon contract forbids. Per-level medallions («Искатель»,
 * «Хранитель», «Проводник света», «Магистр света») are requested on the ticket.
 */
const LEVEL_MEDALLION = require('../../../../assets/gamification/shield-star.png');

// CORE-946 — presentation is keyed off the EVENT's level, not the live profile.
import { LEVEL_NAME_FALLBACK, resolveLevelUpPresentation } from './levelUpPresentation';
import { uiFont } from '../../../styles/agapellaFonts';

interface Props {
  visible: boolean;
  onClose: () => void;
  event: GamificationEvent | null;
}

export const LevelUpModal: React.FC<Props> = ({ visible, onClose, event }) => {
  // Mount the body — and with it `useLiveCapabilities` (an interval + foreground
  // poller) and the profile fetch — ONLY while the popup is actually open, so the
  // celebration never leaves a background poller running on the dashboard.
  return (
    <Modal transparent visible={visible && !!event} animationType="fade" onRequestClose={onClose}>
      {visible && event ? <LevelUpBody event={event} onClose={onClose} /> : null}
    </Modal>
  );
};

const LevelUpBody: React.FC<{ event: GamificationEvent; onClose: () => void }> = ({ event, onClose }) => {
  const { colors, isDark } = useTheme();
  const [level, setLevel] = useState<GamificationLevel | null>(null);

  // Effective Live rights, kept fresh by the store. The level-up handler already
  // invalidated the cache (useGamificationCelebration), so this reads the WIDENED
  // set without a relogin; it degrades to the safe baseline on flag-off/error.
  const { data: caps, loading: capsLoading } = useLiveCapabilities();
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;

  // The high-water of the last set of rights we showed this user — read once when
  // the popup opens, so the diff is against the PRE-level-up set (the store's
  // post-level-up refetch never overwrites what we compare against here).
  const [highWater, setHighWater] = useState<readonly string[] | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    storage.getJSON<string[]>(liveCapabilityHighWaterKey(currentUserId))
      .then((hw) => { if (alive) setHighWater(hw ?? null); })
      .catch(() => { if (alive) setHighWater(null); });
    return () => { alive = false; };
  }, [currentUserId]);

  // What actually opened at this level — from the backend contract, never a
  // hardcoded list. `undefined` high-water = still reading → no list yet.
  const unlocked = useMemo(
    () => (highWater === undefined ? [] : selectNewCapabilities(highWater, caps)),
    [highWater, caps],
  );
  const capsReady = highWater !== undefined && !capsLoading;
  // CORE-509 — и что откроется ДАЛЬШЕ. Тот же контракт, прочитанный вперёд:
  // сервер присылает для каждой закрытой возможности минимальный этап, который
  // её открывает. Ролевые возможности сюда не попадают — этап никогда не
  // становится ролью мастера, и обещать её нельзя.
  const upcoming = useMemo(() => selectUpcomingUnlocks(caps), [caps]);
  const showList = unlocked.length > 0;
  // Level layer contributed but no explicit diff (first level-up / repeat) → a
  // soft, non-committal line. Never shown for a master or a flag-off answer, so
  // «Проводник света» is never dressed as the confirmed master role and no
  // disabled feature is promised.
  const showSoft = capsReady && !showList && caps.levelLayerEnabled && caps.creatorRole !== 'master';

  // Pull the fresh profile when the popup opens — it already reflects the new
  // level, giving the authoritative name + XP + progress. Best-effort.
  useEffect(() => {
    let alive = true;
    api.getGamificationProfile()
      .then((p) => { if (alive) setLevel(p.level); })
      .catch(() => { /* fall back to the event copy */ });
    return () => { alive = false; };
  }, []);

  // Advance the high-water to the widened set as the popup closes, but only when
  // the level layer really answered — so a repeated event lists nothing and a
  // flag-off answer never seeds the baseline with the bare floor.
  const advanceHighWater = () => {
    if (capsReady && caps.levelLayerEnabled && caps.creatorRole !== 'master') {
      void storage.setJSON(liveCapabilityHighWaterKey(currentUserId), caps.capabilities);
    }
  };
  const handleClose = () => { advanceHighWater(); onClose(); };
  const handleHost = () => {
    advanceHighWater();
    onClose();
    navigateTo('CreateLiveSession');
  };

  // CORE-946 — the celebrated level (title, badge colour, name) comes from the
  // event's referenceKey, never the live profile: a QA-injected or refresh-racing
  // event must congratulate the level it names. The profile only feeds the XP bar,
  // and only when it is already at that same level (`progressLevel`).
  const { code, color, levelName, progressLevel } = resolveLevelUpPresentation(event, level);
  const style = { color };

  const xpForNext = progressLevel?.xpForNextLevel ?? null;
  const xpSpan = progressLevel ? progressLevel.xpIntoLevel + (xpForNext ?? 0) : 0;
  const pct = xpSpan > 0 ? Math.round(Math.max(0, Math.min(1, progressLevel!.xpIntoLevel / xpSpan)) * 100) : 100;
  const atMax = progressLevel != null && xpForNext == null;

  // CORE-776 — «права обновились после level-up» + «показ новой доступной
  // функции». Один раз за открытие попапа, когда реальный диф уже посчитан
  // (capsReady) и что-то открылось. Якорь воронки к `level_up` (CORE-711):
  // openedKeys — те же ключи, что видит пользователь, без свободного текста.
  const trackedShownRef = useRef(false);
  useEffect(() => {
    if (trackedShownRef.current || !capsReady || !showList) return;
    trackedShownRef.current = true;
    const ctx = capabilityContext(caps);
    trackCapabilitiesLeveledUp({ context: ctx, openedKeys: unlocked, newLevelCode: code });
    trackCapabilityUnlockShown({ context: ctx, capabilityKeys: unlocked });
  }, [capsReady, showList, unlocked, caps, code]);

  return (
      <View style={styles.scrim}>
        <View style={[styles.card, { backgroundColor: colors.surfaceElevated, borderColor: style.color + '55' }]}>
          <TouchableOpacity onPress={handleClose} style={[styles.close, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#F3F4F6' }]}>
            <Ionicons name="close" size={18} color={colors.textMuted} />
          </TouchableOpacity>

          <ScrollView contentContainerStyle={styles.body}>
            <Text style={[styles.congrats, { color: colors.textPrimary }]}>Поздравляем!</Text>
            <Text style={[styles.reached, { color: colors.textMuted }]}>Ты достиг(ла) нового уровня</Text>

            {/* Новый бейдж — Canonical v4 medallion (CORE-272, Veronika 2026-07-30).
                Was an MCI glyph inside a tinted, glowing ring; the canonical PNG
                bakes the disc and rim in, and the set forbids recolouring, cropping
                and added effects — so it renders bare, at one size, same asset in
                both themes. */}
            <Image
              source={LEVEL_MEDALLION}
              style={styles.medallion}
              resizeMode="contain"
              accessibilityLabel={`Медальон уровня «${levelName}»`}
            />
            <Text style={[styles.levelName, { color: style.color }]} numberOfLines={1}>{levelName}</Text>

            {/* BE-rendered flavour copy when present. */}
            {event.message ? (
              <Text style={[styles.message, { color: colors.textSecondary }]}>{event.message}</Text>
            ) : null}

            {/* Прогресс — toward the next level (or a max-level note). */}
            <View style={[styles.progressBlock, { borderColor: colors.border }]}>
              {progressLevel && !atMax ? (
                <>
                  <View style={styles.progressHead}>
                    <Text style={[styles.progressLabel, { color: colors.textMuted }]} numberOfLines={1}>
                      До следующего уровня{xpForNext != null ? `: ${xpForNext.toLocaleString('ru-RU')} XP` : ''}
                    </Text>
                    <Text style={[styles.progressPct, { color: colors.textMuted }]}>{pct}%</Text>
                  </View>
                  <View style={[styles.track, { backgroundColor: colors.border }]}>
                    <View style={[styles.fill, { width: `${pct}%`, backgroundColor: colors.solar }]} />
                  </View>
                  <Text style={[styles.progressXp, { color: colors.textMuted }]}>{(progressLevel.xp).toLocaleString('ru-RU')} XP · Свет Души</Text>
                </>
              ) : (
                <Text style={[styles.progressLabel, { color: colors.textMuted }]}>
                  {atMax ? 'Максимальный уровень достигнут — вершина Пути Света' : 'Продолжай практику, чтобы расти дальше'}
                </Text>
              )}
            </View>

            {/* CORE-774 — «что ты открыл(а)» is now the REAL set of Live
                capabilities that opened at this level, diffed from the backend
                contract (no hardcoded perks, no level→rights matrix on the FE).
                Empty diff on the level layer → a soft, non-committal line; flag
                off / master → nothing (no false promise, level ≠ master role). */}
            {showList ? (
              <>
                <Text style={[styles.microLabel, { color: colors.textMuted }]}>ЧТО ТЫ ОТКРЫЛ(А) ДЛЯ LIVE</Text>
                <View style={styles.unlocks}>
                  {unlocked.map((cap) => (
                    <View key={cap} style={styles.unlockRow}>
                      <Ionicons name="lock-open-outline" size={18} color={style.color} />
                      <Text style={[styles.unlockText, { color: colors.textPrimary }]} numberOfLines={2}>
                        {CAPABILITY_LABELS[cap]}
                      </Text>
                    </View>
                  ))}
                </View>
              </>
            ) : showSoft ? (
              <Text style={[styles.softNote, { color: colors.textSecondary }]}>
                Тебе стали доступны новые возможности проведения Live-сессий
              </Text>
            ) : null}

            {/* CORE-509 — «что открывается на уровнях выше»: строго ПОСЛЕ того,
                что открылось сейчас. Порядок здесь и есть смысл — сначала то,
                что у человека уже есть, и только потом дорога вперёд, иначе
                поздравление превращается в витрину недоступного. */}
            {upcoming.length > 0 ? (
              <>
                <Text style={[styles.microLabel, { color: colors.textMuted }]}>ЧТО ОТКРОЕТСЯ ДАЛЬШЕ</Text>
                <View style={styles.unlocks}>
                  {upcoming.map((stage) => (
                    <View key={stage.levelCode} style={styles.aheadGroup}>
                      <Text style={[styles.aheadStage, { color: colors.textSecondary }]}>
                        {`На этапе «${LEVEL_NAME_FALLBACK[stage.levelCode] ?? stage.levelCode}»`}
                      </Text>
                      {stage.capabilities.map((cap) => (
                        <View key={cap} style={styles.unlockRow}>
                          <Ionicons name="lock-closed-outline" size={16} color={colors.textMuted} />
                          <Text style={[styles.aheadText, { color: colors.textSecondary }]} numberOfLines={2}>
                            {CAPABILITY_LABELS[cap]}
                          </Text>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              </>
            ) : null}

            {/* CTA into the actual Live create-flow — shown only when hosting
                features actually opened, so we never send a user toward a screen
                that would just reject what the level did not grant. */}
            {showList ? (
              <TouchableOpacity
                style={[styles.host, { backgroundColor: style.color }]}
                onPress={handleHost}
                activeOpacity={0.9}
              >
                <Ionicons name="radio-outline" size={18} color="#fff" />
                <Text style={styles.hostText}>Провести Live-сессию</Text>
              </TouchableOpacity>
            ) : null}

            {/* Actions. */}
            <View style={styles.actions}>
              <TouchableOpacity style={[styles.secondary, { borderColor: colors.border }]} onPress={handleClose} activeOpacity={0.85}>
                <Text style={[styles.secondaryText, { color: colors.textPrimary }]}>Продолжить путь</Text>
              </TouchableOpacity>
              {/* CORE-274 — same share component as the other gamification
                  surfaces. Was a hand-rolled ShareModal whose link pointed at
                  the profile root, so a shared level-up opened «Лента». */}
              <GamificationShareButton
                type="level"
                id={code}
                filled
                fallbackMessage={`Я на новом уровне Пути Света в Agapella — «${levelName}»`}
                style={styles.primary}
              />
            </View>
          </ScrollView>
        </View>
      </View>
  );
};

const styles = StyleSheet.create({
  scrim: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', padding: 16 },
  card: { width: '100%', maxWidth: 460, maxHeight: '92%', borderRadius: 22, borderWidth: 1, overflow: 'hidden' },
  close: { position: 'absolute', top: 14, right: 14, zIndex: 2, width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  body: { padding: 24, paddingTop: 32, alignItems: 'center', gap: 6 },
  congrats: { ...uiFont(500, 700), fontSize: 26 },
  reached: { ...uiFont(400), fontSize: 13 },
  // One CSS size for every medallion, inside Veronika's 40–80 px recommendation.
  medallion: { width: 80, height: 80, marginVertical: 14 },
  levelName: { ...uiFont(500, 700), fontSize: 26, marginTop: 2 },
  message: { ...uiFont(400), fontSize: 14, lineHeight: 20, textAlign: 'center', marginTop: 6 },
  progressBlock: { alignSelf: 'stretch', borderWidth: 1, borderRadius: 14, padding: 14, gap: 8, marginTop: 14 },
  progressHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 6 },
  progressLabel: { ...uiFont(400), fontSize: 12, flex: 1 },
  progressPct: { ...uiFont(500, 700), fontSize: 12 },
  progressXp: { ...uiFont(400), fontSize: 11 },
  track: { height: 6, borderRadius: 3, overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 3 },
  microLabel: { ...uiFont(500, 700), alignSelf: 'stretch', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 16 },
  // CORE-774 — the real capability list: one row per opened right.
  unlocks: { alignSelf: 'stretch', gap: 8, marginTop: 10 },
  unlockRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  unlockText: { ...uiFont(500, 600), flex: 1, fontSize: 14 },
  // CORE-509 — «дальше по пути»: тише, чем открытое сейчас, и без CTA.
  aheadGroup: { alignSelf: 'stretch', gap: 8, marginTop: 4 },
  aheadStage: { ...uiFont(500, 700), fontSize: 12 },
  aheadText: { ...uiFont(400), flex: 1, fontSize: 13 },
  softNote: { ...uiFont(400), alignSelf: 'stretch', fontSize: 14, lineHeight: 20, textAlign: 'center', marginTop: 16 },
  host: { alignSelf: 'stretch', flexDirection: 'row', gap: 8, paddingVertical: 13, borderRadius: 12, alignItems: 'center', justifyContent: 'center', marginTop: 18 },
  hostText: { ...uiFont(500, 700), color: '#fff', fontSize: 14 },
  actions: { flexDirection: 'row', gap: 10, marginTop: 14, alignSelf: 'stretch' },
  secondary: { flex: 1, paddingVertical: 13, borderRadius: 12, borderWidth: 1, alignItems: 'center' },
  secondaryText: { ...uiFont(500, 700), fontSize: 13 },
  primary: { flex: 1, flexDirection: 'row', gap: 6, paddingVertical: 13, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  primaryText: { ...uiFont(500, 700), color: '#fff', fontSize: 13 },
});
